import convertDateStrFromDate from "../../utils/convertDateStrFromDate";

export type TDatePreset =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'month'
  | 'year'
  | 'all'
  | 'custom';

export interface IDateRange {
  createdFrom: string; // 'yyyy-MM-dd', '' means unbounded
  createdTill: string;
}

const shiftDays = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
};

const day = (days: number): string => convertDateStrFromDate(shiftDays(days));

// --
// -- Week, Month and Year are rolling windows ending today, not calendar
// -- periods. On a "Recent Orders" screen a calendar "This Month" shows a
// -- single day's orders on the 1st, which is the wrong default for a view
// -- whose job is recency. Today and Yesterday are still single calendar days.
// --
// -- The backend takes createdFrom/createdTill as yyyy-MM-dd and widens them to
// -- start-of-day / end-of-day itself, so both ends are inclusive.
// --
export const DATE_PRESETS: { value: TDatePreset, title: string, range: () => IDateRange }[] = [
  {
    value: 'today',
    title: 'Today',
    range: () => ({ createdFrom: day(0), createdTill: day(0) }),
  },
  {
    value: 'yesterday',
    title: 'Yesterday',
    range: () => ({ createdFrom: day(-1), createdTill: day(-1) }),
  },
  {
    value: 'week',
    title: 'Week',
    range: () => ({ createdFrom: day(-6), createdTill: day(0) }),
  },
  {
    value: 'month',
    title: 'Month',
    range: () => ({ createdFrom: day(-29), createdTill: day(0) }),
  },
  {
    value: 'year',
    title: 'Year',
    range: () => ({ createdFrom: day(-364), createdTill: day(0) }),
  },
  {
    value: 'all',
    title: 'All',
    range: () => ({ createdFrom: '', createdTill: '' }),
  },
];

export const presetRange = (preset: TDatePreset): IDateRange =>
  DATE_PRESETS.find(({ value }) => value === preset)?.range()
    ?? { createdFrom: '', createdTill: '' };

// --
// -- Which preset a range corresponds to, so typing dates by hand that happen
// -- to match "Today" still lights up Today rather than dropping to Custom.
// --
export const presetForRange = ({ createdFrom, createdTill }: IDateRange): TDatePreset =>
  DATE_PRESETS.find(({ range }) => {
    const preset = range();
    return preset.createdFrom === createdFrom && preset.createdTill === createdTill;
  })?.value ?? 'custom';
